import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { supabase } from '@/lib/supabase';
import {
  getAdminPerformance,
  TechAdminPerformanceResponse,
  TechPerformanceRange,
} from '@/lib/tech-api-client';

function formatCents(cents: number): string {
  const dollars = Number(cents || 0) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

const RANGE_OPTIONS: Array<{ key: TechPerformanceRange; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'ytd', label: 'Year to date' },
];

export default function TechPerformancePage() {
  const { accessToken } = useTechAuth();

  const [range, setRange] = useState<TechPerformanceRange>('last30');
  const [data, setData] = useState<TechAdminPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAccessToken = async (): Promise<string> => {
    if (accessToken) return accessToken;

    const { data: session, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) throw sessionErr;

    const token = session.session?.access_token;
    if (!token) throw new Error('Missing auth token. Please sign in again.');

    return token;
  };

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const res = await getAdminPerformance(token, range);
      setData(res);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, range]);

  const rows = useMemo(() => data?.rows || [], [data?.rows]);
  const summary = data?.summary;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Performance</h1>
          <p className="text-gray-600 mt-1">
            Membership sales + spiff totals by technician.
            {data?.range?.start && data?.range?.end
              ? ` (${new Date(data.range.start).toLocaleDateString()} → ${new Date(
                  data.range.end
                ).toLocaleDateString()})`
              : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as TechPerformanceRange)}
            className="px-3 py-2 rounded-xl bg-white border border-gray-200"
            disabled={loading}
          >
            {RANGE_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Memberships sold</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{summary?.memberships_sold ?? '—'}</div>
          {summary?.sale_type_counts && (
            <div className="mt-2 text-xs text-gray-500">
              New: {summary.sale_type_counts.new} • Upgrade: {summary.sale_type_counts.upgrade} • Renewal:{' '}
              {summary.sale_type_counts.renewal}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Spiffs pending</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {summary?.spiffs ? formatCents(summary.spiffs.pending_cents) : '—'}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Spiffs paid</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {summary?.spiffs ? formatCents(summary.spiffs.paid_cents) : '—'}
          </div>
          {summary?.spiffs && (
            <div className="mt-2 text-xs text-gray-500">Cancelled: {formatCents(summary.spiffs.cancelled_cents)}</div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">Technicians</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Technician</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4">Sales</th>
                <th className="py-2 pr-4">Spiffs (pending)</th>
                <th className="py-2 pr-4">Spiffs (paid)</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No activity for this time range.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const tech = row.technician;

                  return (
                    <tr key={tech.id} className="border-t border-gray-100">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{tech.full_name}</div>
                        <div className="text-xs text-gray-500">
                          {tech.email}
                          {tech.successware_employee_code ? ` • ${tech.successware_employee_code}` : ''}
                        </div>
                      </td>
                      <td className="py-3 pr-4">{tech.role}</td>
                      <td className="py-3 pr-4">
                        <div className="font-medium text-gray-900">{row.memberships_sold}</div>
                        <div className="text-xs text-gray-500">
                          New: {row.sale_type_counts.new} • Upgrade: {row.sale_type_counts.upgrade} • Renewal:{' '}
                          {row.sale_type_counts.renewal}
                        </div>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">{formatCents(row.spiffs.pending_cents)}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">{formatCents(row.spiffs.paid_cents)}</td>
                      <td className="py-3">
                        <span
                          className={
                            tech.is_active
                              ? 'inline-flex px-2 py-1 rounded-lg bg-green-100 text-green-800 text-xs'
                              : 'inline-flex px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs'
                          }
                        >
                          {tech.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
